// Helper to parse hex strings into RGB objects
function hexToRgb(hex: string) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// The main scoring engine. Note we take a flat RGBA pixel array.
export function calculateLayerAccuracy(
  pixels: Uint8ClampedArray | number[],
  targetHex: string,
  visibleRect?: { minX: number; maxX: number; minY: number; maxY: number },
  pixelWidth?: number
): number {
  if (!targetHex) return 100;

  const targetRgb = hexToRgb(targetHex);
  if (!targetRgb) return 0;

  // Convert RGB to HSL (0-1 range for all)
  function rgbToHsl(r: number, g: number, b: number) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return [h, s, l];
  }

  const targetHsl = rgbToHsl(targetRgb.r, targetRgb.g, targetRgb.b);
  const isTargetColorful = targetHsl[1] > 0.15 && targetHsl[2] > 0.1 && targetHsl[2] < 0.9;

  let totalScore = 0;
  let pixelCount = 0;
  const step = 16; // 4 pixels * 4 channels

  for (let i = 0; i < pixels.length; i += step) {
    if (visibleRect && pixelWidth) {
      const pixelIndex = i / 4;
      const x = pixelIndex % pixelWidth;
      const y = Math.floor(pixelIndex / pixelWidth);
      if (x < visibleRect.minX || x > visibleRect.maxX || y < visibleRect.minY || y > visibleRect.maxY) {
        continue;
      }
    }

    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    const a = pixels[i + 3];

    if (a > 10) {
      // Euclidean distance in RGB space
      const dist = Math.sqrt(
        Math.pow(r - targetRgb.r, 2) +
        Math.pow(g - targetRgb.g, 2) +
        Math.pow(b - targetRgb.b, 2)
      );

      const normalizedDist = Math.min(dist / 200, 1.0);
      let score = 100 * (1 - Math.pow(normalizedDist, 2));

      if (isTargetColorful) {
        const hsl = rgbToHsl(r, g, b);
        let hueDiff = Math.abs(hsl[0] - targetHsl[0]);
        if (hueDiff > 0.5) hueDiff = 1 - hueDiff;

        // 0.05 hue shift (~18 degrees) is a noticeably different color.
        // Apply a steep quadratic penalty to destroy the Euclidean proximity score if the hue is wrong.
        if (hueDiff > 0.05) {
          const penalty = Math.pow((hueDiff - 0.05) * 8, 2) * 100;
          score = Math.max(0, score - penalty);
        }
      }

      totalScore += score;
      pixelCount++;
    }
  }

  return pixelCount > 0 ? (totalScore / pixelCount) : 0;
}
